import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Mail,
  Send,
  Users,
  CheckCircle,
  AlertCircle,
  Clock,
  Plus,
  Edit,
  Trash2,
  Eye,
  RefreshCw,
  BarChart3,
  FileText,
  UserCheck,
  UserX,
  Archive
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CRMCampaignsPage() {
  const { token } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [emailLists, setEmailLists] = useState([]);
  const [verificationStats, setVerificationStats] = useState(null);

  // New campaign form state
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    description: '',
    email_subject: 'Contact Information Update Request',
    email_body: `Dear [Name],

We hope this email finds you well.

As part of our regular data maintenance, we are updating our contact and company records. We want to ensure that our information remains accurate and up to date.

According to our records, we currently have the following contact details:

Name: [Name]
Email: [Email]
Company: [Company]

Please take a moment to confirm or update your details by clicking the link below:

[Update your details]

The form also includes a brief company information section where you may update accounting and business details if necessary.

This helps us maintain accurate records and ensures smooth communication.

The form takes approximately 2-3 minutes to complete.

Thank you for your cooperation.

Best regards,
Europacific Team`,
    target_type: 'all',
    target_ids: [],
    manual_emails: '',
    include_kyc_link: true,
    drip_enabled: false,
    drip_days: [0, 14, 30]
  });

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  // Fetch campaigns
  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/crm/campaigns`, {
        headers: getAuthHeaders()
      });
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Fetch email lists
  const fetchEmailLists = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/crm/email-lists`, {
        headers: getAuthHeaders()
      });
      setEmailLists(response.data.lists || []);
    } catch (error) {
      console.error('Error fetching email lists:', error);
    }
  }, [getAuthHeaders]);

  // Fetch verification stats
  const fetchVerificationStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/crm/contacts/verification-stats`, {
        headers: getAuthHeaders()
      });
      setVerificationStats(response.data);
    } catch (error) {
      console.error('Error fetching verification stats:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (token) {
      fetchCampaigns();
      fetchEmailLists();
      fetchVerificationStats();
    }
  }, [token, fetchCampaigns, fetchEmailLists, fetchVerificationStats]);

  // Create campaign
  const handleCreateCampaign = async () => {
    try {
      const payload = {
        ...newCampaign,
        manual_emails: newCampaign.manual_emails
          ? newCampaign.manual_emails.split(/[,;\n]/).map(e => e.trim()).filter(Boolean)
          : []
      };

      await axios.post(`${API_URL}/crm/campaigns`, payload, {
        headers: getAuthHeaders()
      });

      toast.success('Campaign created successfully');
      setShowCreateDialog(false);
      fetchCampaigns();

      // Reset form
      setNewCampaign({
        name: '',
        description: '',
        email_subject: 'Contact Information Update Request',
        email_body: newCampaign.email_body,
        target_type: 'all',
        target_ids: [],
        manual_emails: '',
        include_kyc_link: true,
        drip_enabled: false,
        drip_days: [0, 14, 30]
      });
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error('Failed to create campaign');
    }
  };

  // Send campaign
  const handleSendCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to send this campaign? This will send emails to all recipients.')) {
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/crm/campaigns/${campaignId}/send`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Campaign sending started! ${response.data.total_recipients} recipients`);
      fetchCampaigns();
    } catch (error) {
      console.error('Error sending campaign:', error);
      toast.error(error.response?.data?.detail || 'Failed to send campaign');
    }
  };

  // Delete campaign
  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/crm/campaigns/${campaignId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error('Failed to delete campaign');
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { variant: 'secondary', icon: FileText },
      sending: { variant: 'warning', icon: Clock },
      sent: { variant: 'success', icon: CheckCircle },
      failed: { variant: 'destructive', icon: AlertCircle }
    };
    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Email Campaigns</h2>
          <p className="text-muted-foreground">
            Manage contact reconfirmation and KYC update campaigns
          </p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              New Campaign
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Email Campaign</DialogTitle>
              <DialogDescription>
                Create a new contact reconfirmation or KYC update campaign
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Campaign Name</Label>
                  <Input
                    placeholder="Q1 2026 Contact Verification"
                    value={newCampaign.name}
                    onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Target Audience</Label>
                  <Select
                    value={newCampaign.target_type}
                    onValueChange={(value) => setNewCampaign({ ...newCampaign, target_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Contacts</SelectItem>
                      <SelectItem value="email_list">Specific Email List</SelectItem>
                      <SelectItem value="manual">Manual Entry</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {newCampaign.target_type === 'email_list' && (
                <div className="space-y-2">
                  <Label>Select Email Lists</Label>
                  <Select
                    value={newCampaign.target_ids[0] || ''}
                    onValueChange={(value) => setNewCampaign({ ...newCampaign, target_ids: [value] })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose email list..." />
                    </SelectTrigger>
                    <SelectContent>
                      {emailLists.map((list) => (
                        <SelectItem key={list.id} value={list.id}>
                          {list.name} ({list.emails?.length || 0} emails)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {newCampaign.target_type === 'manual' && (
                <div className="space-y-2">
                  <Label>Email Addresses (one per line or comma-separated)</Label>
                  <textarea
                    className="w-full h-24 p-2 border rounded-md text-sm"
                    placeholder="email1@example.com&#10;email2@example.com"
                    value={newCampaign.manual_emails}
                    onChange={(e) => setNewCampaign({ ...newCampaign, manual_emails: e.target.value })}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Email Subject</Label>
                <Input
                  value={newCampaign.email_subject}
                  onChange={(e) => setNewCampaign({ ...newCampaign, email_subject: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Email Body</Label>
                <p className="text-xs text-muted-foreground">
                  Use placeholders: [Name], [Email], [Company], [Update your details]
                </p>
                <textarea
                  className="w-full h-64 p-3 border rounded-md text-sm font-mono"
                  value={newCampaign.email_body}
                  onChange={(e) => setNewCampaign({ ...newCampaign, email_body: e.target.value })}
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newCampaign.include_kyc_link}
                    onChange={(e) => setNewCampaign({ ...newCampaign, include_kyc_link: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Include KYC update form link</span>
                </label>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateCampaign} disabled={!newCampaign.name}>
                Create Campaign
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      {verificationStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Contacts</p>
                  <p className="text-2xl font-bold">{verificationStats.total}</p>
                </div>
                <Users className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Verified</p>
                  <p className="text-2xl font-bold text-green-600">{verificationStats.verified}</p>
                </div>
                <UserCheck className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <p className="text-2xl font-bold text-amber-600">{verificationStats.pending}</p>
                </div>
                <Clock className="h-8 w-8 text-amber-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Bounced</p>
                  <p className="text-2xl font-bold text-red-600">{verificationStats.bounced}</p>
                </div>
                <UserX className="h-8 w-8 text-red-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Inactive</p>
                  <p className="text-2xl font-bold text-gray-500">{verificationStats.inactive}</p>
                </div>
                <Archive className="h-8 w-8 text-gray-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Campaigns List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Campaigns
              </CardTitle>
              <CardDescription>
                {campaigns.length} campaign(s)
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchCampaigns}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No campaigns yet</p>
              <p className="text-sm">Create your first campaign to start verifying contacts</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Recipients</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead>Bounced</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{campaign.name}</p>
                        <p className="text-xs text-muted-foreground truncate max-w-xs">
                          {campaign.email_subject}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                    <TableCell>{campaign.stats?.total_recipients || 0}</TableCell>
                    <TableCell className="text-green-600">
                      {campaign.stats?.sent || 0}
                    </TableCell>
                    <TableCell className="text-red-600">
                      {campaign.stats?.bounced || 0}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(campaign.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {campaign.status === 'draft' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleSendCampaign(campaign.id)}
                              className="flex items-center gap-1"
                            >
                              <Send className="h-3 w-3" />
                              Send
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteCampaign(campaign.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </>
                        )}
                        {campaign.status === 'sent' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedCampaign(campaign);
                              setShowPreviewDialog(true);
                            }}
                          >
                            <BarChart3 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Campaign Stats Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Campaign Statistics</DialogTitle>
            <DialogDescription>{selectedCampaign?.name}</DialogDescription>
          </DialogHeader>
          {selectedCampaign && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 border rounded-lg text-center">
                  <p className="text-3xl font-bold text-blue-600">
                    {selectedCampaign.stats?.total_recipients || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Total Recipients</p>
                </div>
                <div className="p-4 border rounded-lg text-center">
                  <p className="text-3xl font-bold text-green-600">
                    {selectedCampaign.stats?.sent || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Sent</p>
                </div>
                <div className="p-4 border rounded-lg text-center">
                  <p className="text-3xl font-bold text-amber-600">
                    {selectedCampaign.stats?.opened || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Opened</p>
                </div>
                <div className="p-4 border rounded-lg text-center">
                  <p className="text-3xl font-bold text-red-600">
                    {selectedCampaign.stats?.bounced || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Bounced</p>
                </div>
              </div>
              {selectedCampaign.send_completed_at && (
                <p className="text-xs text-muted-foreground text-center">
                  Completed: {new Date(selectedCampaign.send_completed_at).toLocaleString()}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
